import { useState, useCallback } from 'react';
import { makePanel, panelEndToward, nearestPanelEnd } from '../utils/panelLayout';

/**
 * Panel tool - lays arena / corral panels end to end.
 *
 * First click sets where the run starts (pinning to a nearby panel end), then
 * each click drops one panel of the selected product's true length toward the
 * cursor and carries on from its far end. Escape ends the run. The direction
 * snaps to 5° steps; hold Shift to place at any angle.
 */
export const usePanelTool = ({ active, choice, screenToWorld, scale, activeFloor, updateActiveFloor, setSelectedItems }) => {
  const [chainStart, setChainStart] = useState(null);
  const [preview, setPreview] = useState(null);

  const snapRadius = 20 / scale;

  const nextPanel = useCallback((cursor, shiftKey) => {
    if (!chainStart || !choice) return null;
    let end = panelEndToward(chainStart, cursor, choice.option.lengthFt, shiftKey ? 0 : 5);
    // Close the loop exactly when the panel lands on another panel's end
    const pinned = nearestPanelEnd(activeFloor?.panels, end, snapRadius);
    if (pinned) end = pinned;
    return makePanel(choice.product, choice.option, chainStart, end);
  }, [chainStart, choice, activeFloor?.panels, snapRadius]);

  const handlePanelPointerDown = useCallback((e) => {
    if (!active || !choice || e.button === 1 || e.button === 2) return false;
    const pos = screenToWorld(e.clientX ?? e.touches?.[0]?.clientX, e.clientY ?? e.touches?.[0]?.clientY);
    if (!chainStart) {
      setChainStart(nearestPanelEnd(activeFloor?.panels, pos, snapRadius) || pos);
      return true;
    }
    const panel = nextPanel(pos, e.shiftKey);
    if (panel) {
      updateActiveFloor(f => ({ ...f, panels: [...(f.panels || []), panel] }));
      setSelectedItems([{ type: 'panel', item: panel }]);
      setChainStart(panel.end);
      setPreview(null);
    }
    return true;
  }, [active, choice, chainStart, screenToWorld, activeFloor?.panels, snapRadius, nextPanel, updateActiveFloor, setSelectedItems]);

  const handlePanelPointerMove = useCallback((e) => {
    if (!active || !chainStart) return false;
    const pos = screenToWorld(e.clientX ?? e.touches?.[0]?.clientX, e.clientY ?? e.touches?.[0]?.clientY);
    setPreview(nextPanel(pos, e.shiftKey));
    return false; // let panning etc. still see the move
  }, [active, chainStart, screenToWorld, nextPanel]);

  const cancelPanelRun = useCallback(() => {
    setChainStart(null);
    setPreview(null);
  }, []);

  return {
    panelPreview: active ? preview : null,
    panelRunActive: active && !!chainStart,
    handlePanelPointerDown,
    handlePanelPointerMove,
    cancelPanelRun,
  };
};

export default usePanelTool;
